import {NgIf} from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {NbIconModule} from '@nebular/theme';
import {CompoApiService} from 'compob2b-libs';
import {SafeUrl} from '@angular/platform-browser';

@Component({
  selector: 'app-secured-image',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div #trigger class="secured-img-container">
      <ng-container *ngIf="blob; else loader">
        <img [src]="blob" alt="" [class]="class"/>
      </ng-container>

      <ng-template #loader>
        <div class="loader">
          <nb-icon icon="loader-outline"/>
        </div>
      </ng-template>
    </div>
  `,
  imports: [
    NgIf,
    NbIconModule,
  ],
  styleUrls: ['secured-image.component.scss']
})
export class SecuredImageComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() lazy = false;

  get src(): string {
    return this._src;
  }

  @Input()
  set src(value: string) {
    if (this._src !== value) {
      this._src = value;
      if (this._src) {
        this.resetAndLoadImage();
      }
    }
  }

  @ViewChild('trigger') trigger!: any;

  inited = false;
  loaded = false;
  blob: SafeUrl;
  observer: IntersectionObserver;
  cdr = inject(ChangeDetectorRef);
  compoApiService = inject(CompoApiService);
  private _src: string;

  ngOnChanges(changes: SimpleChanges) {
    if (this.inited && changes['src'] && !changes['src'].firstChange) {
      this.resetAndLoadImage();
    }
  }

  ngAfterViewInit() {
    this.inited = true;

    if (this.lazy) {
      this.observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting && !this.loaded) {
          this.loadImage();
        }
      }, {
        threshold: 0.1,
        rootMargin: '50px' // предзагрузка за 50px до вьюпорта
      });

      this.observer.observe(this.trigger.nativeElement);
    }
  }

  private resetAndLoadImage() {
    this.blob = null;
    this.loaded = false;

    if (this.isVisible()) {
      this.loadImage();
    }
  }

  private isVisible() {
    if (!this.lazy) {
      return true;
    } else if (this.trigger?.nativeElement) {
      const rect = this.trigger.nativeElement.getBoundingClientRect();
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      );
    }
  }

  private loadImage() {
    if (!this.src || this.loaded) {
      return;
    }

    this.loaded = true;
    this.compoApiService.getDocumentImageBlob(this.src).subscribe((blob) => {
      this.blob = blob;
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy() {
    this.observer?.disconnect();
  }

  get class(): string {
    return this.src?.indexOf('/assets') === 0 ? 'vivid' : '';
  }
}
